import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import EntityPageScaffold from "../components/entity/EntityPageScaffold";
import CrudListToolbar from "../components/entity/CrudListToolbar";
import GlobalTimeNavigatorBar from "../components/GlobalTimeNavigatorBar";
import ViewModeIconToggle from "../components/ViewModeIconToggle";
import VolunteerSectionTabs, { VOLUNTEER_TAB_PARTICIPANTS } from "../components/VolunteerSectionTabs";
import ProfileInfoModal from "../components/ProfileInfoModal";
import VolunteerParticipantsSummaryStatCards from "../components/VolunteerParticipantsSummaryStatCards";
import { useGlobalTimeNavigator } from "../shared/hooks/useGlobalTimeNavigator";
import { useEntityListChrome } from "../shared/hooks/useEntityListChrome";
import { volunteerOpportunityRequestBody } from "../shared/api/volunteerOpportunityContext";
import { getMessageForFetchError } from "../shared/utils/apiError";
import { downloadCsvTextFromString } from "../shared/utils/csvFileHelpers";
import { sortRowsByConfig } from "../shared/utils/sortRowsByConfig";
import { getRepeatLabel } from "../shared/utils/repeatOptions";
import { formatPhoneDisplay } from "../shared/utils/phoneFormat";
import { formatTimestampForTable } from "../shared/utils/dateDisplay";
import {
  formatOpportunityDateTimeRange,
  formatOpportunityDuration,
  formatVolunteerDrillStatusLabel,
  getOpportunityDurationHours,
  getVolunteerParticipantDisplayName,
  normalizeVolunteerOpportunityStatus,
  VOLUNTEER_OPPORTUNITY_STATUS_OPTIONS,
  volunteerDrillChipStyle,
  volunteerDrillClearBtnStyle,
} from "../shared/utils/volunteerOpportunityDisplay";
import {
  matchesVolunteerOpportunityScopeFilters,
  matchesVolunteerParticipantSearch,
} from "../shared/utils/volunteerListFilters";
import { buildVolunteerOpportunitiesPath } from "../shared/utils/volunteerOpportunityNavigation";
import { summarizeVolunteerParticipantRows } from "../shared/utils/volunteerParticipantSummary";
import { buildVolunteerParticipantCsvExportText } from "../shared/utils/volunteerParticipantCsv";
import { displayEmailOrDash } from "../shared/utils/displayEmail";


import { apiFetch } from "../shared/api/apiFetch";

const uiConfig = require("../uiConfig");

const SORT_KEY_TYPES = {
  participant_name: "string",
  signup_date: "date",
  opportunity_name: "string",
  opportunity_category: "string",
  opportunity_status: "string",
};

function VolunteerParticipants() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const opportunityIdFromUrl = (searchParams.get("opportunityId") || "").trim();
  const eventIdFromUrl = (searchParams.get("eventId") || "").trim();
  const categoryFromUrl = (searchParams.get("category") || "").trim();
  const statusFromUrl = (searchParams.get("status") || "").trim().toLowerCase();
  const drillSourceFromUrl = (searchParams.get("drillSource") || "").trim();
  const navViewFromUrl = (searchParams.get("navView") || "").trim().toLowerCase();
  const navDateFromUrl = (searchParams.get("navDate") || "").trim();

  const userInfo = JSON.parse(sessionStorage.getItem("userInfo"));
  const account_id = userInfo?.account_id;
  const user_id = userInfo?._id;
  const role = userInfo?.role;
  const volunteerSession = useMemo(
    () => ({ account_id, user_id, role }),
    [account_id, user_id, role]
  );
  const serverUrl = uiConfig.serverInfo.serverUrl;

  const [participants, setParticipants] = useState([]);
  const [loading, setLoading] = useState(false);
  const [opportunities, setOpportunities] = useState([]);
  const [statusFilter, setStatusFilter] = useState("");
  const [opportunityFilter, setOpportunityFilter] = useState(() =>
    opportunityIdFromUrl ? opportunityIdFromUrl : "__all__"
  );
  const [eventFilterName, setEventFilterName] = useState("");
  const [opportunityFilterName, setOpportunityFilterName] = useState("");
  const [profileParticipant, setProfileParticipant] = useState(null);
  const [exporting, setExporting] = useState(false);

  const timeNav = useGlobalTimeNavigator({
    initialView: ["day", "week", "month", "future", "all"].includes(navViewFromUrl) ? navViewFromUrl : "all",
    initialFocusDateYmd: navDateFromUrl || undefined,
  });

  const {
    searchTerm,
    setSearchTerm,
    sortConfig,
    bannerError,
    setBannerError,
    viewMode,
    setViewMode,
    handleSort,
    confirmDialog,
    setConfirmDialog,
    closeConfirmDialog,
  } = useEntityListChrome({ initialViewMode: "table" });

  useEffect(() => {
    if (drillSourceFromUrl === "volunteer_opportunities") {
      if (statusFromUrl === "open") setStatusFilter("Open");
      else if (statusFromUrl === "closed") setStatusFilter("Closed");
      else setStatusFilter("");
      if (["day", "week", "month", "future", "all"].includes(navViewFromUrl)) {
        timeNav.setView(navViewFromUrl);
      }
      if (navDateFromUrl) timeNav.setFocusDateYmd(navDateFromUrl);
    }
    if (opportunityIdFromUrl) setOpportunityFilter(opportunityIdFromUrl);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- apply drill query params once
  }, [drillSourceFromUrl, statusFromUrl, navViewFromUrl, navDateFromUrl, opportunityIdFromUrl]);

  const loadParticipants = useCallback(async () => {
    if (!account_id) {
      setParticipants([]);
      return;
    }
    setLoading(true);
    try {
      const resp = await apiFetch(serverUrl + "/listVolunteerOpportunityParticipants", {
        method: "POST",
        body: JSON.stringify(volunteerOpportunityRequestBody(volunteerSession)),
      });
      const data = resp.ok ? await resp.json() : [];
      setParticipants(Array.isArray(data) ? data : []);
    } catch (err) {
      setBannerError(getMessageForFetchError(err));
      setParticipants([]);
    } finally {
      setLoading(false);
    }
  }, [account_id, serverUrl, volunteerSession, setBannerError]);

  const loadOpportunities = useCallback(async () => {
    if (!account_id) {
      setOpportunities([]);
      return;
    }
    try {
      const resp = await apiFetch(serverUrl + "/listVolunteerOpportunities", {
        method: "POST",
        body: JSON.stringify(volunteerOpportunityRequestBody(volunteerSession)),
      });
      const data = resp.ok ? await resp.json() : [];
      setOpportunities(Array.isArray(data) ? data : []);
    } catch {
      setOpportunities([]);
    }
  }, [account_id, serverUrl, volunteerSession]);

  useEffect(() => {
    loadParticipants();
    loadOpportunities();
  }, [loadParticipants, loadOpportunities]);

  useEffect(() => {
    if (!eventIdFromUrl || !account_id) {
      setEventFilterName("");
      return;
    }
    let cancelled = false;
    apiFetch(serverUrl + "/getEvent", {
      method: "POST",
      body: JSON.stringify({ account_id, _id: eventIdFromUrl }),
    })
      .then((resp) => (resp.ok ? resp.json() : null))
      .then((data) => {
        if (!cancelled) setEventFilterName(data?.name != null ? String(data.name).trim() : "");
      })
      .catch(() => {
        if (!cancelled) setEventFilterName("");
      });
    return () => {
      cancelled = true;
    };
  }, [eventIdFromUrl, account_id, serverUrl]);

  useEffect(() => {
    const oid = opportunityFilter !== "__all__" ? opportunityFilter : opportunityIdFromUrl;
    if (!oid) {
      setOpportunityFilterName("");
      return;
    }
    const fromList = (opportunities || []).find((o) => String(o._id) === String(oid));
    if (fromList?.name) {
      setOpportunityFilterName(String(fromList.name).trim());
      return;
    }
    const fromRow = (participants || []).find((p) => String(p.volunteer_opportunity_id) === String(oid));
    setOpportunityFilterName(fromRow?.opportunity_name ? String(fromRow.opportunity_name).trim() : "");
  }, [opportunityFilter, opportunityIdFromUrl, opportunities, participants]);

  function clearDrillFilters() {
    setStatusFilter("");
    setOpportunityFilter("__all__");
    timeNav.goToday();
    setSearchParams({}, { replace: true });
  }

  const opportunityScopeId = opportunityFilter !== "__all__" ? opportunityFilter : "";

  const filteredRows = useMemo(() => {
    const scope = {
      timeNav,
      eventId: eventIdFromUrl,
      statusFilter,
      category: categoryFromUrl,
      opportunityId: opportunityScopeId,
    };
    let filtered = (participants || []).filter((row) => {
      if (!matchesVolunteerOpportunityScopeFilters(row, scope)) return false;
      return matchesVolunteerParticipantSearch(row, searchTerm);
    });
    if (!sortConfig?.key) return filtered;
    if (sortConfig.key === "participant_name") {
      const dir = sortConfig.direction === "asc" ? 1 : -1;
      filtered = [...filtered].sort((a, b) => {
        const av = getVolunteerParticipantDisplayName(a).toLowerCase();
        const bv = getVolunteerParticipantDisplayName(b).toLowerCase();
        if (av < bv) return -1 * dir;
        if (av > bv) return 1 * dir;
        return 0;
      });
      return filtered;
    }
    if (sortConfig.key === "opportunity_duration") {
      const dir = sortConfig.direction === "asc" ? 1 : -1;
      filtered = [...filtered].sort((a, b) => {
        const av =
          getOpportunityDurationHours(a.opportunity_start_datetime, a.opportunity_end_datetime) ??
          Number.NEGATIVE_INFINITY;
        const bv =
          getOpportunityDurationHours(b.opportunity_start_datetime, b.opportunity_end_datetime) ??
          Number.NEGATIVE_INFINITY;
        if (av < bv) return -1 * dir;
        if (av > bv) return 1 * dir;
        return 0;
      });
      return filtered;
    }
    return sortRowsByConfig(filtered, sortConfig, SORT_KEY_TYPES);
  }, [
    participants,
    searchTerm,
    sortConfig,
    timeNav,
    eventIdFromUrl,
    statusFilter,
    categoryFromUrl,
    opportunityScopeId,
  ]);
  const listSummary = useMemo(() => summarizeVolunteerParticipantRows(filteredRows), [filteredRows]);
  const hasDrillFilters =
    drillSourceFromUrl === "volunteer_opportunities" &&
    (opportunityIdFromUrl ||
      categoryFromUrl ||
      statusFromUrl ||
      eventIdFromUrl ||
      navViewFromUrl ||
      navDateFromUrl);

  const opportunityOptions = useMemo(() => {
    const map = new Map();
    for (const o of opportunities || []) {
      if (o?._id) map.set(String(o._id), o.name || "Opportunity");
    }
    for (const p of participants || []) {
      const id = p?.volunteer_opportunity_id;
      if (id && !map.has(String(id))) map.set(String(id), p.opportunity_name || "Opportunity");
    }
    return Array.from(map.entries())
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => String(a.name).localeCompare(String(b.name)));
  }, [opportunities, participants]);

  function exportFilteredParticipantsCsv() {
    if (filteredRows.length === 0) {
      setConfirmDialog({
        open: true,
        title: "Export",
        message: "No participant records match your current filters.",
        mode: "alert",
        onConfirm: null,
      });
      return;
    }
    setExporting(true);
    try {
      const csv = buildVolunteerParticipantCsvExportText(filteredRows);
      const datePart = new Date().toISOString().slice(0, 10);
      downloadCsvTextFromString(`volunteer-participants-export-${datePart}.csv`, csv);
      setBannerError("");
    } catch (err) {
      setBannerError(getMessageForFetchError(err, "Error exporting CSV."));
    } finally {
      setExporting(false);
    }
  }

  return (
    <>
    <EntityPageScaffold
      title="Volunteer Participants"
      titleTabs={<VolunteerSectionTabs activeTab={VOLUNTEER_TAB_PARTICIPANTS} />}
      subtitle={
        hasDrillFilters || eventIdFromUrl || opportunityIdFromUrl ? (
          <span style={{ display: "block" }}>
            Volunteers signed up for opportunities. Search by name, email, phone, or opportunity.
            <span
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                flexWrap: "wrap",
                marginTop: 6,
                fontSize: 12,
                color: "#495057",
              }}
            >
              {hasDrillFilters ? <span>Filter from Volunteer Opportunities:</span> : null}
              {opportunityIdFromUrl || opportunityFilter !== "__all__" ? (
                <span style={volunteerDrillChipStyle}>
                  Opportunity{opportunityFilterName ? `: ${opportunityFilterName}` : ""}
                </span>
              ) : null}
              {eventIdFromUrl ? (
                <span style={volunteerDrillChipStyle}>
                  Linked to event{eventFilterName ? `: ${eventFilterName}` : ""}
                </span>
              ) : null}
              {categoryFromUrl ? (
                <span style={volunteerDrillChipStyle}>Category: {categoryFromUrl}</span>
              ) : null}
              {formatVolunteerDrillStatusLabel(statusFromUrl, statusFilter) ? (
                <span style={volunteerDrillChipStyle}>
                  Status: {formatVolunteerDrillStatusLabel(statusFromUrl, statusFilter)}
                </span>
              ) : null}
              {timeNav.title ? (
                <span style={volunteerDrillChipStyle}>Period: {timeNav.title}</span>
              ) : null}
              {hasDrillFilters || opportunityIdFromUrl || eventIdFromUrl ? (
                <button type="button" onClick={clearDrillFilters} style={volunteerDrillClearBtnStyle}>
                  Clear filter
                </button>
              ) : null}
            </span>
          </span>
        ) : (
          "Volunteers signed up for opportunities. Search by name, email, phone, or opportunity."
        )
      }
      bannerError={bannerError}
      onDismissBanner={() => setBannerError("")}
      confirmDialog={confirmDialog}
      onConfirmModal={() => {
        const fn = confirmDialog.onConfirm;
        closeConfirmDialog();
        if (fn) fn();
      }}
      onCancelModal={closeConfirmDialog}
      pageTopAfterModal={
        <>
          <CrudListToolbar
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            searchPlaceholder="Search participant name, email, phone, opportunity…"
            onClearSearch={() => setSearchTerm("")}
            showCsvImport={false}
            showNew={false}
            showDelete={false}
            showViewToggle={false}
            middleSlot={
              <>
                <label htmlFor="vp-opportunity-filter" className="toolbar-filter-group">
                  <span className="toolbar-label">Opportunity:</span>
                  <select
                    id="vp-opportunity-filter"
                    value={opportunityFilter}
                    onChange={(e) => {
                      const v = e.target.value;
                      setOpportunityFilter(v);
                      const next = new URLSearchParams(searchParams);
                      if (v === "__all__") next.delete("opportunityId");
                      else next.set("opportunityId", v);
                      setSearchParams(next, { replace: true });
                    }}
                    className="input input-min-180"
                  >
                    <option value="__all__">All</option>
                    {opportunityOptions.map((o) => (
                      <option key={o.id} value={o.id}>
                        {o.name}
                      </option>
                    ))}
                  </select>
                </label>
                <label htmlFor="vp-status-filter" className="toolbar-filter-group">
                  <span className="toolbar-label">Status:</span>
                  <select
                    id="vp-status-filter"
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="input input-min-130"
                  >
                    <option value="">All</option>
                    {VOLUNTEER_OPPORTUNITY_STATUS_OPTIONS.map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </select>
                </label>
              </>
            }
            viewMode={viewMode}
            onViewModeChange={setViewMode}
          />
          <div className="toolbar toolbar--volunteer-participants-metrics">
            <VolunteerParticipantsSummaryStatCards
              participantCount={listSummary.participantCount}
              totalVolunteeringHours={listSummary.totalVolunteeringHours}
              className="cards-wrapper--toolbar-inline"
            />
            <span className="toolbar-metrics-spacer" aria-hidden="true" />
            <GlobalTimeNavigatorBar
              variant="inline"
              title={timeNav.title}
              view={timeNav.view}
              onViewChange={timeNav.setView}
              onToday={timeNav.goToday}
              onPrev={timeNav.movePrev}
              onNext={timeNav.moveNext}
            />
            <button
              type="button"
              className="btn btn-md btn-primary-outline"
              disabled={filteredRows.length === 0 || exporting}
              onClick={() => exportFilteredParticipantsCsv()}
            >
              {exporting
                ? "Exporting…"
                : `Export CSV${filteredRows.length > 0 ? ` (${filteredRows.length})` : ""}`}
            </button>
            <span className="toolbar-divider" />
            <ViewModeIconToggle value={viewMode} onChange={setViewMode} ariaLabel="View" />
          </div>
        </>
      }
      list={
        <>
          <div className="table-scroll">
          {loading ? <p className="empty-message">Loading…</p> : null}
          {!loading && filteredRows.length === 0 ? (
            <p className="empty-message">No participants match your filters.</p>
          ) : null}
          {!loading && viewMode === "table" && filteredRows.length > 0 ? (
            <div className="table-wrapper">
              <table>
                <thead>
                  <tr>
                    <th className="sortable" onClick={() => handleSort("participant_name")}>
                      Participant
                    </th>
                    <th className="sortable" onClick={() => handleSort("signup_date")}>
                      Signed up
                    </th>
                    <th className="sortable" onClick={() => handleSort("opportunity_name")}>
                      Opportunity
                    </th>
                    <th className="sortable" onClick={() => handleSort("opportunity_duration")}>
                      Volunteering Hours
                    </th>
                    <th className="sortable" onClick={() => handleSort("opportunity_category")}>
                      Category
                    </th>
                    <th className="sortable" onClick={() => handleSort("opportunity_status")}>
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRows.map((row) => (
                    <tr key={row._id || `${row.user_id}-${row.volunteer_opportunity_id}`}>
                      <td>
                        <button
                          type="button"
                          className="btn-link"
                          onClick={() => setProfileParticipant(row)}
                        >
                          {getVolunteerParticipantDisplayName(row)}
                        </button>
                      </td>
                      <td style={{ whiteSpace: "nowrap" }}>{formatTimestampForTable(row.signup_date)}</td>
                      <td>
                        <button
                          type="button"
                          className="btn-link"
                          onClick={() =>
                            navigate(
                              buildVolunteerOpportunitiesPath({
                                detail: row.volunteer_opportunity_id,
                              })
                            )
                          }
                        >
                          {row.opportunity_name || "—"}
                        </button>
                      </td>
                      <td style={{ whiteSpace: "nowrap" }}>
                        {formatOpportunityDuration(
                          row.opportunity_start_datetime,
                          row.opportunity_end_datetime
                        )}
                      </td>
                      <td>{row.opportunity_category || "—"}</td>
                      <td>{normalizeVolunteerOpportunityStatus(row.opportunity_status)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}
          {!loading && viewMode === "card" && filteredRows.length > 0 ? (
            <div className="cards-wrapper">
              {filteredRows.map((row) => (
                <div key={row._id || `${row.user_id}-${row.volunteer_opportunity_id}`} className="card">
                  <div className="card-header">
                    <div className="card-header-content">
                      <button
                        type="button"
                        className="btn-link card-title"
                        onClick={() => setProfileParticipant(row)}
                      >
                        {getVolunteerParticipantDisplayName(row)}
                      </button>
                      {(() => {
                        const emailDisp = displayEmailOrDash(row.email);
                        return emailDisp !== "—" ? <div className="panel-hint">{emailDisp}</div> : null;
                      })()}
                    </div>
                  </div>
                  <div className="card-meta-muted">
                    Signed up {formatTimestampForTable(row.signup_date)}
                    {row.phone ? ` · ${formatPhoneDisplay(row.phone)}` : ""}
                  </div>
                  <div className="card-meta-muted">
                    <button
                      type="button"
                      className="btn-link"
                      style={{ padding: 0, font: "inherit", color: "inherit", textDecoration: "underline" }}
                      onClick={() =>
                        navigate(
                          buildVolunteerOpportunitiesPath({
                            detail: row.volunteer_opportunity_id,
                          })
                        )
                      }
                    >
                      {row.opportunity_name || "Opportunity"}
                    </button>
                    {row.opportunity_category ? ` · ${row.opportunity_category}` : ""}
                  </div>
                  <div className="card-meta-muted">
                    {formatOpportunityDateTimeRange(
                      row.opportunity_start_datetime,
                      row.opportunity_end_datetime
                    )}{" "}
                    ·{" "}
                    {formatOpportunityDuration(
                      row.opportunity_start_datetime,
                      row.opportunity_end_datetime
                    )}{" "}
                    · {normalizeVolunteerOpportunityStatus(row.opportunity_status)} ·{" "}
                    {getRepeatLabel(row.opportunity_repeat_rule)}
                  </div>
                  {row.opportunity_location ? (
                    <div className="panel-hint card-notes">{row.opportunity_location}</div>
                  ) : null}
                </div>
              ))}
            </div>
          ) : null}
          </div>
        </>
      }
    />
    <ProfileInfoModal
      open={!!profileParticipant}
      onClose={() => setProfileParticipant(null)}
      firstName={profileParticipant?.first_name}
      lastName={profileParticipant?.last_name}
      email={profileParticipant?.email}
      phone={profileParticipant?.phone}
      birthday={profileParticipant?.birthday}
      gender={profileParticipant?.gender}
      grade={profileParticipant?.grade}
      school={profileParticipant?.school}
      createdDate={profileParticipant?.created_date}
    />
    </>
  );
}

export default VolunteerParticipants;
